import { Settled } from "@/sdk/transport/batcher"
import { codedError, protocolError } from "@/sdk/transport/errors"

type Slot = { index?: unknown; item?: unknown; code?: string; message?: string }

/**
 * Maps a bulk envelope back onto the inputs that produced it. `results` and
 * `errors` must partition the input range exactly — anything else is a
 * protocol error for the whole flush, since no slot can be trusted. Results
 * carry their `index`; an endpoint that predates the envelope lists bare
 * successes in input order instead, and those take the indices the errors left.
 */
export function fanOut<T>(
  count: number,
  data: { results?: unknown; errors?: unknown } | undefined,
  result: (item: unknown) => T
): Settled<T>[] {
  const results = data?.results
  const errors = data?.errors
  if (!Array.isArray(results)) throw protocolError("results is not an array")
  if (!Array.isArray(errors)) throw protocolError("errors is not an array")

  const settled: Array<Settled<T> | undefined> = new Array(count)
  const claim = (slot: Slot): number => {
    const index = slot?.index
    if (
      !Number.isInteger(index) ||
      (index as number) < 0 ||
      (index as number) >= count
    ) {
      throw protocolError(`index ${String(index)} is out of range`)
    }
    if (settled[index as number])
      throw protocolError(`index ${index} answered twice`)
    return index as number
  }

  errors.forEach((slot: Slot) => {
    settled[claim(slot)] = {
      ok: false,
      error: codedError(slot.code ?? "error", slot.message ?? "Request failed")
    }
  })

  const indexed = results.filter(
    (slot: Slot) => slot !== null && typeof slot === "object" && "index" in slot
  ).length
  if (indexed === results.length) {
    results.forEach((slot: Slot) => {
      settled[claim(slot)] = { ok: true, value: result(slot.item) }
    })
  } else if (indexed === 0) {
    let next = 0
    results.forEach(item => {
      while (next < count && settled[next]) next++
      if (next >= count) throw protocolError("more results than inputs")
      settled[next] = { ok: true, value: result(item) }
    })
  } else {
    throw protocolError("results mix indexed and bare slots")
  }

  return Array.from({ length: count }, (_, index) => {
    const outcome = settled[index]
    if (!outcome) throw protocolError(`index ${index} is unanswered`)
    return outcome
  })
}
